'use client';

import { Badge, Box, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { memo, useCallback, useState } from 'react';
import { LuArrowUpRight, LuSettings } from 'react-icons/lu';

import { type SupplierPayoutNodeData, PickerMode } from '@features/workflows/types';
import { useWorkflowCallbacks } from '@features/workflows/WorkflowContext';

import NodeHoverActions from './NodeHoverActions';

interface Props extends NodeProps {
  data: SupplierPayoutNodeData;
}

const SupplierPayoutNode = ({ id, data, dragging }: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const { onOpenPicker, onOpenConfig, onDeleteNode } = useWorkflowCallbacks();
  const { label, description, amountUsdc } = data;

  const handleAdd = useCallback((nodeId: string) => onOpenPicker(nodeId, PickerMode.Add), [onOpenPicker]);
  const handleReplace = useCallback((nodeId: string) => onOpenPicker(nodeId, PickerMode.Replace), [onOpenPicker]);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleClick = useCallback(() => onOpenConfig(id), [onOpenConfig, id]);
  const handleConfig = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenConfig(id);
    },
    [onOpenConfig, id],
  );

  return (
    <Box position="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <NodeHoverActions
        nodeId={id}
        isHovered={isHovered && dragging !== true}
        onAdd={handleAdd}
        onReplace={handleReplace}
        onDelete={onDeleteNode}
      />
      <Flex
        direction="column"
        bg="surface.background"
        borderWidth="1px"
        borderColor="border.light"
        borderRadius="xl"
        w="280px"
        overflow="hidden"
        cursor="pointer"
        boxShadow="xs"
        _hover={{ boxShadow: 'sm' }}
        onClick={handleClick}
      >
        <Flex px={3} pt={2} pb={1} alignItems="center" justifyContent="space-between">
          <Badge size="xs" px={2} borderRadius="full" bg="green.100" color="green.700" fontSize="2xs" fontWeight="semibold">
            Payout
          </Badge>
          <IconButton
            size="2xs"
            variant="ghost"
            color="text.secondary"
            _hover={{ color: 'text.primary' }}
            aria-label="Configure payout"
            onClick={handleConfig}
          >
            <LuSettings size={13} />
          </IconButton>
        </Flex>
        <Flex px={3} pb={3} gap={2} alignItems="flex-start">
          <Icon mt="2px" color="text.secondary" flexShrink={0}>
            <LuArrowUpRight size={16} />
          </Icon>
          <Flex direction="column" gap={0.5} minW={0}>
            <Text fontSize="sm" fontWeight="medium" truncate>
              {label}
            </Text>
            <Text fontSize="xs" color="text.secondary" truncate>
              {amountUsdc != null ? `${amountUsdc.toLocaleString('en-US')} USDC` : description}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 8,
          height: 8,
          background: 'var(--chakra-colors-gray-400)',
          border: '2px solid var(--chakra-colors-gray-200)',
        }}
      />
    </Box>
  );
};

export default memo(SupplierPayoutNode);
